import { GetStateResponse } from '@iwoca/lapi-client/edge';
import _ from 'lodash';

import { getBundleDate, getBundleHashName } from '../../devOpsHelper/DevOps';
import { cloneStates, mergeStates } from '../../iwProvider/StoreHelper';
import { apiReducer } from '../API.reducer';
import {
    getAPIMethodFromAction,
    getAPINameFromAction,
    getAPIStatusFromAction
} from '../HelperAPI';
import * as actions from './LendingAPI.actions';
import {
    API_GET_IWOCAPAY_CAPTURED_PAYMENTS,
    API_GET_IWOCAPAY_PAY_LINKS
} from '../../myAccount/iwocaPay/api/LendingAPI.actions';

type TState = {
    [k: string]: any;
};
type TAction =
    | {
          type: 'api/GET_CUSTOMER_STATE [edit]';
          data: GetStateResponse['data'];
      }
    | {
          type: string;
          data?: any;
          response: any;
      };

const initialState = {
    account_statement: { data: {} },
    accounts: { data: {} },
    address_lookup: { data: {} },
    application: { data: {} },
    customer_state: { data: {} },
    document_upload: { data: {} },
    draw_down_repayment_schedule: { data: {} },
    early_repayment_simulation: { data: {} },
    formal_offers: { data: {} },
    fund_product_offer_request: { data: {} },
    funding_requirement: { data: {} },
    iwocapay_pay_link: { data: {} },
    iwocapay_pay_links: { data: [] },
    iwocapay_captured_payments: { data: [] },
    loan_documents: { data: {} },
    offer_configuration: { data: {} },
    onfido_sms: { data: {} },
    online_account: { data: [] },
    payment_method: { data: {} },
    product_offers: { data: {} },
    offers: { data: {} },
    rbl_estimated_schedule: { data: {} },
    site_context: { data: {} }
};

const dynamicAPIActions = {
    [actions.API_GET_SITE_CONTEXT]: apiReducer,
    [actions.API_GET_CUSTOMER_STATE]: apiReducer,
    [actions.API_PUT_CUSTOMER_STATE]: apiReducer,
    [actions.API_PUT_AGREE_CUSTOMER_DOCUMENT]: apiReducer,
    [actions.API_GET_PRODUCT_OFFERS]: apiReducer,
    [actions.API_GET_OFFERS]: apiReducer,
    [actions.API_GET_FORMAL_OFFERS]: apiReducer,
    [actions.API_POST_FORMAL_OFFERS]: apiReducer,
    [actions.API_POST_DECLINE_APPLICATION]: apiReducer,
    [actions.API_GET_PAYMENT_METHOD]: apiReducer,
    [actions.API_POST_PAYMENT_METHOD]: apiReducer,
    [actions.API_GET_EARLY_REPAYMENT_SIMULATION]: apiReducer,
    [actions.API_GET_LOAN_DOCUMENTS]: apiReducer,
    [actions.API_GET_FUNDING_REQUIREMENT]: apiReducer,
    [actions.API_GET_ADDRESS_LOOKUP]: apiReducer,
    [actions.API_GET_COMPANY_LOOKUP]: apiReducer,
    [API_GET_IWOCAPAY_PAY_LINKS]: apiReducer,
    [API_GET_IWOCAPAY_CAPTURED_PAYMENTS]: apiReducer,
    [actions.API_POST_ONFIDO_SMS]: apiReducer,
    [actions.API_POST_ID_CHECK_SMS]: apiReducer,
    [actions.API_GET_APPLICATION]: apiReducer,
    [actions.API_PUT_APPLICATION]: apiReducer,
    [actions.API_GET_ACCOUNTS]: apiReducer,
    [actions.API_GET_RBL_ESTIMATED_SCHEDULE]: apiReducer,
    [actions.API_POST_FUND_PRODUCT_OFFER_REQUEST]: apiReducer,
    [actions.API_GET_DRAW_DOWN_REPAYMENT_SCHEDULE]: apiReducer
};

// eslint-disable-next-line import/no-default-export
export default function (
    state: TState = mergeStates(initialState),
    // @ts-expect-error A test relies on the default value but now doesn't match the type.
    action: TAction = {}
) {
    const dynamicNewState = dynamicAPIStateReducer(state, action);

    if (dynamicNewState) {
        return dynamicNewState;
    } else {
        return staticAPIStateReducer(state, action);
    }
}

function staticAPIStateReducer(state: TState, action: TAction) {
    switch (action.type) {
        case 'api/GET_CUSTOMER_STATE [edit]':
            const editedCustomerStateData = buildEditedCustomerState(
                state.customer_state.data,
                action.data
            );
            return _.assign(state, {
                customer_state: {
                    data: editedCustomerStateData,
                    edited: true
                }
            });

        default:
            return state;
    }
}

function buildEditedCustomerState(oldRootData: any, rootDataToUpdate: any) {
    const currentRootState = cloneStates(oldRootData);
    const newRootData = mergeStates(rootDataToUpdate, {
        ui: {
            version: {
                bundleDate: getBundleDate(),
                bundleHashName: getBundleHashName()
            }
        }
    });

    _.transform(
        newRootData,
        (currentRootState, childRootValue, rootKey) => {
            if (_.isObject(childRootValue)) {
                currentRootState[rootKey] = {
                    ...currentRootState[rootKey],
                    ...childRootValue
                };
            } else {
                //TODO: in case of version as a string, prefer an explicit check instead
                currentRootState[rootKey] = childRootValue;
            }
        },
        currentRootState
    );

    return currentRootState;
}

function dynamicAPIStateReducer(state: TState, action: TAction) {
    const decomposedApiAction = getGenericAction(action.type);

    // @ts-expect-error Need to correctly type dynamicAPIActions and decomposedApiAction.
    if (dynamicAPIActions[decomposedApiAction.key]) {
        // @ts-expect-error
        return dynamicAPIActions[decomposedApiAction.key](
            decomposedApiAction,
            state,
            action
        );
    }
}

function getGenericAction(action: string) {
    const apiMethod = getAPIMethodFromAction(action);
    const apiName = getAPINameFromAction(action);
    const apiStatus = getAPIStatusFromAction(action);
    const key = `api/${apiMethod}_${apiName}`;

    return { key, apiName, apiMethod, apiStatus };
}
